import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DateFormat,
  EventCard,
  EventCardProps,
  FAIconName,
  getAgeFromDob,
  useToastMessage,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  AppointmentStatus,
  CalendarResource,
  CalendarResourceMapper,
  FETCH_APPOINTMENT_TYPES_ERROR,
  FETCH_APPOINTMENTS_ERROR,
  MAX_QUERY_PERIODS,
  SchedulerStep,
  UNKNOWN_AVAILABILITY_RULE_ERROR,
} from '~/lib/constants/calendar.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { ParticipantTypes } from '~/lib/constants/schedule.constants';
import { AppointmentAttendeesAllowed } from '~/lib/constants/scheduling.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { isPatientBookingEnabled } from '~/lib/hooks/useAppointmentBookingEligibility';
import useCalendarApi from '~/lib/hooks/useCalendarApi';
import useCalendarElement from '~/lib/hooks/useCalendarElement';
import { isBaseQueryError } from '~/lib/types/api/isQueryError';
import {
  addTimezoneCodeToParticipantSecondaryText,
  extractHostTimezone,
  filterSupportAppointments,
  formatDateWithTimezone,
  generateAvailabilityQuery,
  getAllowedParticipants,
  getAppointmentEndTime,
  getDurationInMinutes,
  getInvitedParticipants,
  getPatientSupportFreePeriods,
  getPrimaryProviderTypeForAppointmentType,
  getQueryPeriods,
  Recurrence,
  removeTimezoneCodeFromParticipantSecondaryText,
  sortParticipants,
  toAttendee,
} from '~/lib/util/calendar.util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  getTimezoneValueToCodeMapper,
  useTimezoneLabelToCodeMapper,
} from '~/lib/util/timezone.util';
import { toISO, toISORemoveMillis } from '~/lib/utils';
import Loading from '~/pages/loading';

import SchedulerV2 from './SchedulerV2';

import './scheduler.css';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientScheduleAppointmentEvents } from '~/analytics/patientEvents';
import { useGetProviderTimezonesEnumQuery } from '~/store/services/enumerations';
import {
  useLazyGetAppointmentTypesQuery,
  useLazyGetAccountsQuery,
} from '~/store/services/schedule';
import {
  useGetAppointmentDateTimeDetailsQuery,
  useSaveAppointmentMutation,
} from '~/store/services/appointment';
import { getRecurrenceDisplay } from '~/lib/util/schedule/schedule.util';
import { capitalizeFirstLetter } from '~/lib/common';
import { getAttendeeName } from './schedule/day-view/EventDayCard';
import { UserRole } from '~/lib/constants/patients.constants';
import {
  getCategorizedPatientAndSupportAppointments,
  formatAppointmentTimeFromDate,
  hasMatchingProviderTypeName,
} from './util';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import {
  getIntervalInMinutesByAppointmentType,
  ScheduledApppointmentsResponse,
} from './EditAppointment/util';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';

const localTimezone = DateTime.now().zoneName;

interface ResetAvailabilityParams {
  appointmentType?: AppointmentType;
  element_token?: string;
  ignoreAvailabilityRuleSettings?: boolean;
  newHostParticipant?: Participant;
  newPatientParticipant?: Participant;
  newProviderParticipants?: Participant[];
  newSupportParticipants?: Participant[];
}

type PartialCalendarAccount = Pick<
  CalendarAccount,
  'calendarId' | 'externalId' | 'sub'
>;

export interface SchedulerProps {
  careTeamMembers: CareTeamMember[];
  currentDate?: string;
  id: string;
  isOpen: boolean;
  onClose: (
    appointmentStartDateTime?: string,
    shouldShowWarningModal?: boolean,
  ) => void;
  onError: (id: string, message: string, statusCode?: number) => void;
  onAppointmentTypeSelected?: (ruleExternalId: string) => void;
  patient: Patient;
  programName: string;
  showLoadingSpinner: boolean;
}

interface GenerateElementTokenProps {
  appointmentType: AppointmentType;
  isPatientAttending: boolean;
  providerCalendarsInfo: UserCalendarInfo[];
  supportIds: string[];
  hostParticipant: Participant;
  hostSub: string;
}

const Scheduler = ({
  careTeamMembers,
  isOpen,
  onClose,
  onError,
  onAppointmentTypeSelected,
  patient,
  programName,
  showLoadingSpinner,
  currentDate,
}: SchedulerProps) => {
  const { trackEvent } = useAnalytics();
  const { supports = [] } = patient;

  const TOP_LEVEL_ID = 'scheduler';
  const shouldEnablePatientBooking = useMemo<boolean>(
    () => isPatientBookingEnabled(patient),
    [patient],
  );

  // Appointment Details State
  const [appointmentTypeExternalId, setAppointmentTypeExternalId] =
    useState<string>(null);
  const [ignoreAvailabilitySettings, setIgnoreAvailabilitySettings] =
    useState<boolean>(false);
  const [
    disableIgnoreAvailabilitySettings,
    setDisableIgnoreAvailabilitySettings,
  ] = useState<boolean>(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<Recurrence>(
    Recurrence.DOES_NOT_REPEAT,
  );
  const recurrenceFrequencyRef = useRef<Recurrence>();
  recurrenceFrequencyRef.current = recurrenceFrequency;
  const handleRecurrenceSelect = (recurrence: Recurrence): void => {
    setRecurrenceFrequency(recurrence);
  };
  const [appointmentStartDateTime, setAppointmentStartDateTime] =
    useState<string>(null);
  const [step, setStep] = useState<SchedulerStep>(SchedulerStep.SELECT_SLOT);
  const [isPendingAvailabilityQuery, setIsPendingAvailabilityQuery] =
    useState(true);

  // Error State
  const [noEligibleHostsError, setNoEligibleHostsError] =
    useState<boolean>(false);

  const { data: timezones } = useGetProviderTimezonesEnumQuery();

  const timezoneValueToCodeMapper = useMemo<Record<string, string>>(
    () => getTimezoneValueToCodeMapper(timezones),
    [timezones],
  );

  const timezoneLabelToCodeMapper = useTimezoneLabelToCodeMapper(timezones);
  const { createToast } = useToastMessage();

  const handleToast = () => {
    createToast({
      icon: FAIconName.checkRegular,
      id: `${TOP_LEVEL_ID}__success`,
      text: 'Appointment scheduled',
      variant: 'success',
    });
  };

  const withTimezone = (
    text: string,
    timezoneValue: string,
    timezoneLabel?: string,
  ): string => {
    const tzCode =
      timezoneValueToCodeMapper[timezoneValue] ??
      timezoneLabelToCodeMapper[timezoneLabel];
    return addTimezoneCodeToParticipantSecondaryText(text, tzCode);
  };

  const withoutTimezone = (attendee: Participant): string =>
    removeTimezoneCodeFromParticipantSecondaryText(attendee?.secondaryText);

  // Participant State
  const [hostParticipant, setHostParticipant] = useState<Participant>(null);
  const [patientParticipant, setPatientParticipant] = useState<Participant>(
    !shouldEnablePatientBooking
      ? null
      : {
          id: patient.userId,
          isChecked: false,
          primaryText: getPreferredFullName(
            patient.chosenName,
            patient.firstName,
            patient.lastName,
          ),
          secondaryText: withTimezone(
            'Patient',
            patient.timezoneValue,
            patient.timezone,
          ),
        },
  );

  const setDefaultPatientParticipant = (isChecked?: boolean) =>
    setPatientParticipant(
      !shouldEnablePatientBooking
        ? null
        : {
            id: patient.userId,
            isChecked: isChecked ?? false,
            primaryText: getPreferredFullName(
              patient.chosenName,
              patient.firstName,
              patient.lastName,
            ),
            secondaryText: withTimezone(
              'Patient',
              patient.timezoneValue,
              patient.timezone,
            ),
          },
    );

  useEffect(() => {
    setDefaultPatientParticipant();
  }, [patient.userId]);

  const [providerParticipants, setProviderParticipants] = useState<
    Participant[]
  >([]);
  const [supportParticipants, setSupportParticipants] = useState<Participant[]>(
    [],
  );

  const getAppointmentAttendee = (participantsList: Participant[]) => {
    return participantsList
      .map((participant: Participant) =>
        toAttendee(
          participant.sub
            ? {
                ...participant,
                secondaryText: withoutTimezone(participant),
              }
            : participant,
          null,
          withoutTimezone(participant),
        ),
      )
      .filter((attendee: AppointmentAttendee) => attendee?.externalId);
  };

  const attendees = useMemo(() => {
    const hostObj = toAttendee({
      ...hostParticipant,
      secondaryText: withoutTimezone(hostParticipant),
    });

    const providerObj = getAppointmentAttendee(providerParticipants) ?? [];
    const supportObj = getAppointmentAttendee(supportParticipants) ?? [];

    const patientAttendee = patientParticipant?.isChecked
      ? {
          externalId: patient?.userId,
          name:
            getAttendeeName(patient?.name) ||
            `${capitalizeFirstLetter(patient?.chosenName || patient?.firstName)} ${capitalizeFirstLetter(patient?.lastName)}`,
          userType: UserRole.PATIENT,
        }
      : null;

    return [
      {
        avatarUrl: hostObj?.avatar?.thumbnailUrl ?? hostObj?.avatar?.url,
        externalId: hostObj?.externalId,
        isHost: true,
        name: getAttendeeName(hostObj?.name),
        userType: hostObj?.providerType,
        oooDate: getLatestOutOfOfficeEndDate(hostObj?.outOfOffice),
      },
      ...(patientAttendee ? [patientAttendee] : []),
      ...providerObj.map((provider) => ({
        avatarUrl: provider?.avatar?.thumbnailUrl ?? provider?.avatar?.url,
        externalId: provider?.externalId,
        name: getAttendeeName(provider?.name),
        userType: provider?.providerType,
        oooDate: getLatestOutOfOfficeEndDate(provider?.outOfOffice),
      })),
      ...supportObj.map((support) => ({
        externalId: support?.externalId,
        name: getAttendeeName(support?.name),
        userType: support?.providerType || support?.relationToPatient,
      })),
    ];
  }, [
    hostParticipant,
    providerParticipants,
    supportParticipants,
    patient,
    patientParticipant,
  ]);

  // Reccurence conflicts
  const [conflicts, setConflicts] = useState<string[]>([]);
  const [isLoadingConflicts, setIsLoadingConflicts] = useState<boolean>(false);
  const [availabilityQuery, setAvailabilityQuery] =
    useState<CalendarResourceConfigAvailabilityQuery>();
  const [newPatientParticipant, setNewPatientParticipant] =
    useState<Participant>();
  const [invitedSupports, setInvitedSupports] = useState<string[]>([]);
  const {
    elementToken,
    generateElementToken,
    options,
    setHostManagedAvailability,
    setOptions,
  } = useCalendarElement(CalendarResource.DATE_TIME_PICKER_V2);
  const { appointmentRecurrenceCheck } = useCalendarApi();
  const { currentUser, calendarAccount, permissions } = useGlobalContext();
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];
  const { providerTypeName: myProviderTypeName, userId: myExternalId } =
    currentUser;
  const {
    calendarId: myCalendarId,
    profileConnected: isMyProfileConnected,
    sub: mySub,
  } = calendarAccount ?? {};

  const currentUserTeamMember = careTeamMembers?.find(
    ({ providerExternalId }) => providerExternalId === myExternalId,
  );
  const isEligibleHost = isMyProfileConnected && !!currentUserTeamMember;

  const [
    getAppointmentTypes,
    {
      data: appointmentTypesData,
      error: getAppointmentTypesError,
      isLoading: isAppointmentTypesLoading,
    },
  ] = useLazyGetAppointmentTypesQuery();

  const now = useMemo(() => DateTime.now(), []);

  const shouldSkipAppointmentDetailsOrDateDetailsApi =
    !isOpen ||
    !patient.userId ||
    (!isSchedulingAdmin && !(isEligibleHost && myProviderTypeName));

  const getAppointmentDetailsAndDateTimeDetailsApiCommonProps = {
    endDateISO: now.plus({ months: 6 }).toISO(),
    patientId: patient.userId,
    startDateISO: now.minus({ hours: 2 }).toISO(),
  };

  const {
    data: patientAppointmentsDateTimeData,
    isError: isPatientAppointmentsError,
    error: patientAppointmentsError,
    isLoading: isPatientAppointmentsLoading,
  } = useGetAppointmentDateTimeDetailsQuery(
    getAppointmentDetailsAndDateTimeDetailsApiCommonProps,
    {
      skip: shouldSkipAppointmentDetailsOrDateDetailsApi,
    },
  );

  if (isOpen) {
    if (
      getAppointmentTypesError &&
      isBaseQueryError(getAppointmentTypesError)
    ) {
      onError(
        'get-appointment-types',
        FETCH_APPOINTMENT_TYPES_ERROR,
        getAppointmentTypesError.status as number,
      );
    } else if (
      isPatientAppointmentsError &&
      isBaseQueryError(patientAppointmentsError)
    ) {
      onError(
        'get-appointment-details',
        FETCH_APPOINTMENTS_ERROR,
        patientAppointmentsError.status as number,
      );
    }
  }

  /**
   * Organize patient/support appointments into 2 buckets to enable faster evaluation
   *   1. appointments the patient is attending
   *   2. appointments any support is attending (can duplicate #1)
   */
  const { patientAppointments, supportAppointments } = useMemo(() => {
    return getCategorizedPatientAndSupportAppointments(
      patientAppointmentsDateTimeData,
    );
  }, [patientAppointmentsDateTimeData]);

  const sortedAppointmentTypes = useMemo<AppointmentType[]>(() => {
    if (!appointmentTypesData) {
      return [];
    }
    return [...appointmentTypesData].sort((a, b) =>
      a.appointmentType.localeCompare(b.appointmentType),
    );
  }, [appointmentTypesData]);

  const [getCalendarAccounts] = useLazyGetAccountsQuery();

  const [
    saveAppointment,
    { isLoading: isSaveAppointmentLoading, reset: resetSaveAppointmentError },
  ] = useSaveAppointmentMutation();

  const [
    getCareTeamMemberCalendarAccounts,
    { data: careTeamMemberCalendarAccounts },
  ] = useLazyGetAccountsQuery();

  const selectedAppointmentType = useMemo<AppointmentType>(
    () =>
      isPatientAppointmentsLoading
        ? null
        : appointmentTypesData?.find(
            ({ appointmentTypeExtId }: AppointmentType) =>
              appointmentTypeExtId === appointmentTypeExternalId,
          ),
    [
      appointmentTypesData,
      appointmentTypeExternalId,
      isPatientAppointmentsLoading,
    ],
  );

  const assignHostFromCareTeam = (
    careTeamMemberAccount: ProviderCalendarAccount,
  ): void => {
    const {
      avatarDetails,
      displayFirstName,
      displayLastName,
      calendarId,
      externalId,
      firstName,
      lastName,
      providerTypeName,
      sub,
      timezone,
      outOfOfficeItems,
    } = careTeamMemberAccount ?? {};
    if (sub) {
      setHostParticipant({
        avatar: avatarDetails,
        calendarId,
        id: externalId,
        isChecked: true,
        primaryText: getDisplayNameOrLegalNameForProvider(
          displayFirstName,
          firstName,
          displayLastName,
          lastName,
        ),
        secondaryText: addTimezoneCodeToParticipantSecondaryText(
          providerTypeName,
          timezone,
        ),
        sub,
        outOfOfficeItems,
      });
    }
  };

  /**
   * Assign a host based on the following requirements and return the host external id and sub:
   *   1. Select the current user if they are an eligible host
   *   2. If the current user is a scheduling admin
   *      a. Select an eligible host from the care team
   *      b. Select the current user if their calendar profile is connected and
   *         there are no eligible hosts on the care team
   */
  const assignHostOnAppointmentTypeSelect = (
    appointmentType: AppointmentType,
  ): PartialCalendarAccount => {
    let hostDetails: PartialCalendarAccount = null;
    // Make me the host if I'm eligible
    if (
      isEligibleHost &&
      hasMatchingProviderTypeName(appointmentType, myProviderTypeName)
    ) {
      setHostParticipant({
        avatar: currentUserTeamMember.avatarDetails,
        calendarId: myCalendarId,
        id: currentUserTeamMember.providerExternalId,
        isChecked: true,
        primaryText: getDisplayNameOrLegalNameForProvider(
          currentUserTeamMember.displayFirstName,
          currentUserTeamMember.firstName,
          currentUserTeamMember.displayLastName,
          currentUserTeamMember.lastName,
        ),
        secondaryText: addTimezoneCodeToParticipantSecondaryText(
          currentUserTeamMember.providerTypeName,
          currentUserTeamMember.timezone,
        ),
        sub: mySub,
        outOfOfficeItems: currentUserTeamMember?.outOfOfficeItems,
      });

      hostDetails = {
        calendarId: myCalendarId,
        externalId: myExternalId,
        sub: mySub,
      };
    }

    // Determine if someone else can be the host (Scheduling Admins only)
    else if (isSchedulingAdmin) {
      const matchingCareTeamMembers =
        careTeamMembers?.filter(
          ({ providerExternalId, providerTypeName }: CareTeamMember) =>
            providerExternalId !== myExternalId &&
            hasMatchingProviderTypeName(appointmentType, providerTypeName) &&
            careTeamMemberCalendarAccounts?.some(
              ({ externalId }) => externalId === providerExternalId,
            ),
        ) ?? [];

      // Make the first matching care team member the host
      if (matchingCareTeamMembers.length) {
        const defaultHostCalendarAccount = careTeamMemberCalendarAccounts?.find(
          ({ externalId }) =>
            externalId === matchingCareTeamMembers[0].providerExternalId,
        );
        if (defaultHostCalendarAccount) {
          assignHostFromCareTeam({
            ...matchingCareTeamMembers[0],
            ...defaultHostCalendarAccount,
          });
          const { calendarId, externalId, sub } = defaultHostCalendarAccount;
          hostDetails = { calendarId, externalId, sub };
        }
      }
    }

    if (!hostDetails) {
      setHostParticipant(null);
      setNoEligibleHostsError(true);
    }

    return hostDetails;
  };

  const assignDefaultParticipants = (
    appointmentType: AppointmentType,
    hostExternalId: string,
  ): {
    isPatientAttending: boolean;
    providerCalendarsInfo: UserCalendarInfo[];
    supportIds: string[];
  } => {
    let addPatient = false;

    let defaultSupportParticipants: Participant[] = [];
    if (appointmentType?.attendeesAllowed == AppointmentAttendeesAllowed.ALL) {
      const {
        generalSupport: addGeneralSupport,
        patient: addPatientFlag,
        proxySupport: addProxySupport,
      } = getAllowedParticipants(appointmentType.appointmentType, programName);

      addPatient = addPatientFlag;

      if (patientParticipant) {
        setPatientParticipant((participant: Participant) =>
          shouldEnablePatientBooking
            ? {
                ...participant,
                isChecked: addPatientFlag ?? false,
              }
            : null,
        );
      } else {
        setDefaultPatientParticipant(addPatientFlag);
      }

      defaultSupportParticipants = (supports ?? [])
        .filter(({ hasAuthId }) => hasAuthId)
        .map(
          ({
            chosenName,
            externalId: id,
            firstName,
            isProxy,
            lastName,
            relationToPatient,
            timezone,
            timezoneValue,
          }) => ({
            id,
            isChecked:
              (!!isProxy && addProxySupport) || (!isProxy && addGeneralSupport),
            primaryText: getPreferredFullName(chosenName, firstName, lastName),
            secondaryText: withTimezone(
              relationToPatient ||
                (isProxy ? 'Proxy support' : 'General support'),
              timezoneValue,
              timezone,
            ),
          }),
        );

      setSupportParticipants(defaultSupportParticipants);
    }

    if (
      appointmentType?.attendeesAllowed ===
      AppointmentAttendeesAllowed.PROVIDER_ONLY
    ) {
      setPatientParticipant(null);
    }

    const providerCalendarsInfo: UserCalendarInfo[] = [];

    const defaultProviderParticipants = (careTeamMembers ?? []).reduce(
      (participants: Participant[], ctm: CareTeamMember) => {
        const {
          avatarDetails,
          displayFirstName,
          displayLastName,
          firstName,
          lastName,
          providerExternalId,
          providerTypeName,
          timezone,
          outOfOfficeItems,
        } = ctm;

        const { calendarId, sub } =
          careTeamMemberCalendarAccounts?.find(
            ({ externalId }) => externalId === providerExternalId,
          ) ?? {};
        // If the care team member's calendar profile is not connected OR they are the host, don't add them to the list
        if (!sub || providerExternalId === hostExternalId) return participants;

        const hasMatchingProviderType = hasMatchingProviderTypeName(
          appointmentType,
          providerTypeName,
        );

        if (hasMatchingProviderType)
          providerCalendarsInfo.push({ calendarId, sub });

        return participants.concat([
          {
            avatar: avatarDetails,
            calendarId,
            id: providerExternalId,
            isChecked: hasMatchingProviderType,
            isEligibleToHost: hasMatchingProviderType,
            primaryText: getDisplayNameOrLegalNameForProvider(
              displayFirstName,
              firstName,
              displayLastName,
              lastName,
            ),
            secondaryText: addTimezoneCodeToParticipantSecondaryText(
              providerTypeName,
              timezone,
            ),
            sub,
            outOfOfficeItems,
          },
        ]);
      },
      [],
    );

    setProviderParticipants(sortParticipants(defaultProviderParticipants));

    return {
      isPatientAttending: addPatient,
      providerCalendarsInfo,
      supportIds: getInvitedParticipants(defaultSupportParticipants).map(
        ({ id }: Participant) => id,
      ),
    };
  };

  const selectedTimezone = options?.tzid ?? localTimezone;

  /** Update the Cronofy date time picker element availability query */
  const resetAvailability = (params?: ResetAvailabilityParams): void => {
    const {
      appointmentType = selectedAppointmentType,
      element_token = elementToken,
      ignoreAvailabilityRuleSettings = ignoreAvailabilitySettings,
      newHostParticipant = hostParticipant,
      newPatientParticipant = patientParticipant,
      newProviderParticipants = providerParticipants,
      newSupportParticipants = supportParticipants,
    } = params ?? {};

    const start = DateTime.now();

    const end = start.plus({ months: 3 });

    let query_periods: AppointmentDetailTime[] = [
      {
        end: toISORemoveMillis(end),
        start: toISORemoveMillis(start),
      },
    ];

    const invitedSupports = getInvitedParticipants(newSupportParticipants).map(
      (p) => p.id,
    );

    setNewPatientParticipant(newPatientParticipant);
    setInvitedSupports(invitedSupports);

    if (
      (newPatientParticipant?.isChecked && patientAppointments?.length) ||
      (invitedSupports.length && supportAppointments?.length)
    ) {
      query_periods = getPatientSupportFreePeriods({
        end,
        patientAppointments: newPatientParticipant?.isChecked
          ? patientAppointments
          : [],
        start,
        supportAppointments: filterSupportAppointments(
          invitedSupports,
          supportAppointments,
        ),
      }).slice(0, MAX_QUERY_PERIODS);
    }
    const availabilityQueryToUpdate = generateAvailabilityQuery({
      availabilityRule: appointmentType,
      hostCalendarInfo: {
        calendarId: newHostParticipant?.calendarId,
        sub: newHostParticipant?.sub,
      },
      ignoreAvailabilitySettings: ignoreAvailabilityRuleSettings,
      intervalInMinutes: getIntervalInMinutesByAppointmentType(appointmentType),
      providerCalendarsInfo: getInvitedParticipants(
        newProviderParticipants,
      ).map((p) => ({
        calendarId: p.calendarId,
        sub: p.sub,
      })),
      query_periods,
      shouldAllProvidersAvailable:
        appointmentType?.attendeesAllowed ===
        AppointmentAttendeesAllowed.PROVIDER_ONLY,
    });

    setAvailabilityQuery(availabilityQueryToUpdate);
    setOptions({
      ...CalendarResourceMapper[CalendarResource.DATE_TIME_PICKER_V2].options,
      availability_query: availabilityQueryToUpdate,
      callback: handleNotifications,
      element_token,
      tzid: selectedTimezone,
    });
    setIsPendingAvailabilityQuery(false);
  };

  const resetAvailabilityRef = useRef(resetAvailability);
  useEffect(() => {
    resetAvailabilityRef.current = resetAvailability;
  }, [resetAvailability]);

  /** Handle notifications including errors and updating the appointment slot */
  const handleNotifications = useCallback(
    async (data: CalendarEvent) => {
      const { body, slot, type } = data?.notification ?? {};
      // Handle unknown availability rule error
      if (
        type === 'error' &&
        body &&
        Object.entries(body).find(([_k, v]: [string, { key: string }[]]) => {
          return v?.[0]?.key === UNKNOWN_AVAILABILITY_RULE_ERROR;
        })
      ) {
        setHostManagedAvailability({ cronofyRule: null });
        resetAvailabilityRef.current?.({
          ignoreAvailabilityRuleSettings: true,
        });
        setIgnoreAvailabilitySettings(true);
        setDisableIgnoreAvailabilitySettings(true);
      }

      if (type === 'slot_selected') {
        setAppointmentStartDateTime(toISO(slot.start));
      }
    },
    [
      selectedAppointmentType?.appointmentType,
      selectedAppointmentType?.cronofyRule,
    ],
  );

  /**
   * Handle default host and participant assignment, generate a new element token, and reset availability shown
   * in the date time picker
   */

  const resetSelectedProviders = () => {
    setHostParticipant(null);
    setProviderParticipants([]);
    setSupportParticipants([]);
  };

  const generateInitialElementToken = ({
    appointmentType,
    isPatientAttending,
    providerCalendarsInfo,
    supportIds,
    hostParticipant,
    hostSub,
  }: GenerateElementTokenProps) => {
    generateElementToken({
      // Fetch element token that encompasses all care team members in order to
      // avoid extra API call if another care team member is added to the meeting
      callback: (token: ElementTokenData) => {
        if (token?.elementToken?.token) {
          resetAvailability({
            appointmentType,
            element_token: token.elementToken.token,
            ignoreAvailabilityRuleSettings: false,
            newHostParticipant: hostParticipant as Participant,
            newPatientParticipant: {
              isChecked: isPatientAttending,
            } as Participant,
            newProviderParticipants: providerCalendarsInfo.map(
              ({ calendarId, sub }) => ({
                calendarId,
                isChecked: true,
                sub,
              }),
            ) as Participant[],
            newSupportParticipants: (supports ?? [])
              .filter((s1) => supportIds.some((id) => id === s1.externalId))
              .map((s2) => ({
                id: s2.externalId,
                isChecked: true,
              })) as Participant[],
          });
        }
      },
      subs: [
        ...new Set([
          hostSub,
          ...(careTeamMemberCalendarAccounts ?? [])
            .map(({ sub }) => sub)
            .filter(Boolean),
        ]),
      ],
    });
  };

  const handleRuleSelect = (ruleExternalId: string): void => {
    onAppointmentTypeSelected(ruleExternalId);
    setAppointmentStartDateTime(null);
    setAppointmentTypeExternalId(ruleExternalId);
    if (ruleExternalId !== appointmentTypeExternalId) {
      setIsPendingAvailabilityQuery(true);
      setNoEligibleHostsError(false);
      setIgnoreAvailabilitySettings(false);
      setDisableIgnoreAvailabilitySettings(false);

      const appointmentType = appointmentTypesData?.find(
        (type: AppointmentType) => type.appointmentTypeExtId === ruleExternalId,
      );
      if (appointmentType) {
        const { calendarId, externalId, sub } =
          assignHostOnAppointmentTypeSelect(appointmentType) ?? {};
        if (calendarId && externalId && sub) {
          const { isPatientAttending, providerCalendarsInfo, supportIds } =
            assignDefaultParticipants(appointmentType, externalId);

          generateInitialElementToken({
            appointmentType,
            hostParticipant: { calendarId, sub } as Participant,
            hostSub: sub,
            isPatientAttending,
            providerCalendarsInfo,
            supportIds,
          });
        } else {
          setDisableIgnoreAvailabilitySettings(true);
        }
      }
    }
  };
  // Fetch patient appointments and appointment types the first time the scheduler is opened
  // The current user must be a scheduling admin or a care team member with calendar linked
  useEffect(() => {
    if (
      isOpen &&
      patient.userId &&
      (isSchedulingAdmin || (isEligibleHost && myProviderTypeName))
    ) {
      getAppointmentTypes(
        isSchedulingAdmin
          ? undefined
          : getPrimaryProviderTypeForAppointmentType(myProviderTypeName),
      );
    }
  }, [
    isEligibleHost,
    isOpen,
    isSchedulingAdmin,
    myProviderTypeName,
    patient.userId,
  ]);

  // Fetch the care team member calendar accounts the first time the scheduler is opened
  useEffect(() => {
    if (isOpen && careTeamMembers?.length) {
      getCareTeamMemberCalendarAccounts({
        providerExternalIds: careTeamMembers.map(
          ({ providerExternalId }) => providerExternalId,
        ),
      });
    }
  }, [careTeamMembers, isOpen]);

  // Handle default appointment type selection
  useEffect(() => {
    if (
      !isSchedulingAdmin &&
      appointmentTypesData?.length &&
      !appointmentTypeExternalId &&
      careTeamMemberCalendarAccounts?.length
    ) {
      handleRuleSelect(appointmentTypesData[0].appointmentTypeExtId);
    }
  }, [
    appointmentTypeExternalId,
    appointmentTypesData,
    careTeamMemberCalendarAccounts?.length,
    isSchedulingAdmin,
  ]);

  /**
   * Upon selecting a provider from the search, retrieve the selected provider's calendar info
   * to confirm their profile is connected
   *
   * Note: This process will be simplified following completion of https://teamequip.atlassian.net/browse/EQ-8684
   */

  const getCalendarAvailability = (callbackFn, value: string) => {
    getCalendarAccounts({ providerExternalIds: [value] })
      .unwrap()
      .then(() => {
        callbackFn();
      })
      .catch((e) => {
        console.warn('getCalendarAccounts failed', e);
      });
  };

  const handleProviderSelect = (data: ProviderUniversalSelectOption): void => {
    const primaryText = getDisplayNameOrLegalNameForProvider(
      data?.displayFirstName,
      data.firstName,
      data?.displayLastName,
      data.lastName,
    );
    const newProvider = {
      avatar: data?.avatar,
      calendarId: data.calendarId,
      id: data.userId,
      isChecked: true,
      isEligibleToHost: selectedAppointmentType.providerTypeNames.some(
        (providerType) =>
          providerType.toLowerCase() ===
          data.providerTypeDetails.name.toLowerCase(),
      ),
      primaryText,
      secondaryText: addTimezoneCodeToParticipantSecondaryText(
        data.providerTypeDetails.name,
        data.timeZoneDetails.code,
      ),
      sub: data.sub,
      outOfOfficeItems: data.outOfOffice,
    };

    if (!hostParticipant && newProvider.isEligibleToHost) {
      setHostParticipant(newProvider);
      const { isPatientAttending, providerCalendarsInfo, supportIds } =
        assignDefaultParticipants(selectedAppointmentType, newProvider.id);
      generateInitialElementToken({
        appointmentType: selectedAppointmentType,
        isPatientAttending,
        providerCalendarsInfo,
        supportIds,
        hostParticipant: newProvider as Participant,
        hostSub: newProvider.sub,
      });
      handleRuleSelect('');
    } else {
      setProviderParticipants(
        sortParticipants([...providerParticipants, newProvider]),
      );

      const existingSubs = [
        hostParticipant?.sub,
        ...providerParticipants.map(({ sub }) => sub).filter(Boolean),
      ];
      getCalendarAvailability(
        generateElementToken({
          callback: (token: ElementTokenData) => {
            if (token?.elementToken?.token) {
              resetAvailability({
                appointmentType: selectedAppointmentType,
                element_token: token.elementToken.token,
                newHostParticipant: hostParticipant,
                newProviderParticipants: [...providerParticipants, newProvider],
              });
            }
          },
          subs: [...new Set([data.sub, ...existingSubs])],
        }),
        data.userId,
      );
    }
    setAppointmentTypeExternalId(selectedAppointmentType.appointmentTypeExtId);
    trackEvent(PatientScheduleAppointmentEvents.newProviderSelectedFromSearch, {
      Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
    });
  };

  /** Updates participant state and resets availability shown in date time picker */
  const handleUpdateParticipant = (
    id: string,
    isChecked: boolean,
    participantType: ParticipantType,
    altHostParticipant?: Participant,
  ): void => {
    setAppointmentStartDateTime(null);
    let ignoreAvailabilityRuleSettings = ignoreAvailabilitySettings;
    let newPatientParticipant = patientParticipant;
    let newSupportParticipants = supportParticipants;
    let newHostParticipant = hostParticipant;
    let newProviderParticipants = providerParticipants;

    if (participantType === ParticipantTypes.PATIENT) {
      newPatientParticipant = {
        ...patientParticipant,
        isChecked,
      };
      setPatientParticipant(newPatientParticipant);
    } else if (participantType === ParticipantTypes.SUPPORT) {
      newSupportParticipants = supportParticipants.map(
        (participant: Participant) =>
          participant.id === id
            ? { ...participant, isChecked: !participant?.isChecked }
            : participant,
      );
      setSupportParticipants(newSupportParticipants);
    } else if (participantType === ParticipantTypes.HOST) {
      setAppointmentStartDateTime(null);
      setIgnoreAvailabilitySettings(false);
      setDisableIgnoreAvailabilitySettings(false);
      const updatedProviders = sortParticipants([
        { ...hostParticipant, isChecked: false, isEligibleToHost: true },
        ...providerParticipants.filter(
          (eachProvider) => eachProvider.id !== altHostParticipant.id,
        ),
      ]);
      setHostParticipant({ ...altHostParticipant, isChecked: true });
      ignoreAvailabilityRuleSettings = false;
      newHostParticipant = altHostParticipant;
      newProviderParticipants = updatedProviders;
      setProviderParticipants(updatedProviders);
    } else if (participantType === ParticipantTypes.PROVIDER) {
      const providerParticipantIdx = providerParticipants.findIndex(
        (participant: Participant) => participant.id === id,
      );
      const updatedParticipants = [...providerParticipants];

      updatedParticipants[providerParticipantIdx].isChecked = isChecked;
      newProviderParticipants = updatedParticipants;

      setProviderParticipants(newProviderParticipants);
    }

    resetAvailability({
      ignoreAvailabilityRuleSettings,
      newHostParticipant,
      newPatientParticipant,
      newProviderParticipants,
      newSupportParticipants,
    });
  };

  /**
   * Toggle ignore availability settings and update the Cronofy availability query
   *   - If the new value for "ignoreAvailabilitySettings" is FALSE, the "availability_rule_ids" will be
   *     updated to whatever the value of selectedAppointmentType.appointmentType is
   */
  const toggleIgnoreAvailabilitySettings = (): void => {
    const nextIgnoreAvailabilitySettings = !ignoreAvailabilitySettings;
    setHostManagedAvailability({
      cronofyRule: ignoreAvailabilitySettings
        ? (selectedAppointmentType.cronofyRule ??
          selectedAppointmentType.appointmentType)
        : null,
      shouldAllProvidersAvailable:
        selectedAppointmentType?.attendeesAllowed ===
        AppointmentAttendeesAllowed.PROVIDER_ONLY,
    });
    setAppointmentStartDateTime(null);
    setIgnoreAvailabilitySettings(nextIgnoreAvailabilitySettings);
    resetAvailability({
      ignoreAvailabilityRuleSettings: nextIgnoreAvailabilitySettings,
    });
    trackEvent(
      PatientScheduleAppointmentEvents.ignoreAvailabilitySettingsToggled,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );
  };

  const handleSave = (): void => {
    saveAppointment({
      appointmentEndDateTime: getAppointmentEndTime(
        appointmentStartDateTime,
        selectedAppointmentType.durationInMinutes,
      ),
      appointmentStartDateTime,
      appointmentType: selectedAppointmentType.appointmentTypeLabel,
      hostExternalId: hostParticipant.id,
      ignoreAvailabilitySettings,
      isPatientAttending: !!patientParticipant?.isChecked,
      patientExternalId: patient.userId,
      providerAttendeeExternalIds: getInvitedParticipants(
        providerParticipants,
      ).map(({ id }) => id),
      recurrenceFrequency,
      status: AppointmentStatus.SCHEDULED,
      supportAttendeeExternalIds: getInvitedParticipants(
        supportParticipants,
      ).map(({ id }) => id),
      timeZoneId: localTimezone,
    }).then((queryResult) => {
      if (!queryResult.error) {
        onClose(appointmentStartDateTime, false);
        handleToast();
      }
    });

    trackEvent(
      PatientScheduleAppointmentEvents.scheduleAppointmentModalScheduleAppointmentButtonClicked,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );
  };

  const handleNextFromSlotSelect = async (): Promise<void> => {
    setStep(SchedulerStep.REVIEW);

    trackEvent(
      PatientScheduleAppointmentEvents.scheduleAppointmentModalAppointmentConfirmationView,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );

    if (recurrenceFrequencyRef.current !== Recurrence.DOES_NOT_REPEAT) {
      setIsLoadingConflicts(true);
      let patientAppointmentsToCheck: ScheduledApppointmentsResponse[] = [];
      let supportAppointmentsToCheck: ScheduledApppointmentsResponse[] = [];

      if (
        selectedAppointmentType?.attendeesAllowed ===
        AppointmentAttendeesAllowed.PROVIDER_ONLY
      ) {
        patientAppointmentsToCheck = [];
        supportAppointmentsToCheck = [];
      } else {
        if (
          (newPatientParticipant?.isChecked && patientAppointments?.length) ||
          (invitedSupports.length && supportAppointments?.length)
        ) {
          patientAppointmentsToCheck = newPatientParticipant?.isChecked
            ? patientAppointments
            : [];
          supportAppointmentsToCheck = filterSupportAppointments(
            invitedSupports,
            supportAppointments,
          );
        }
      }
      const conflictsRes = await appointmentRecurrenceCheck(
        availabilityQuery,
        elementToken,
        patientAppointmentsToCheck,
        getQueryPeriods(
          getAppointmentEndTime(
            appointmentStartDateTime,
            selectedAppointmentType.durationInMinutes,
          ),
          recurrenceFrequencyRef.current,
          appointmentStartDateTime,
          extractHostTimezone(hostParticipant, timezones),
        ),
        supportAppointmentsToCheck,
      );
      setConflicts(conflictsRes);
      setIsLoadingConflicts(false);
    }
    trackEvent(
      PatientScheduleAppointmentEvents.scheduleAppointmentModalContinueButtonClicked,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );
  };

  const handleNext = async (): Promise<void> => {
    if (step === SchedulerStep.SELECT_SLOT) {
      await handleNextFromSlotSelect();
    } else if (step === SchedulerStep.REVIEW) {
      handleSave();
    }
  };

  const handlePrevious = (): void => {
    if (conflicts.length > 0) setConflicts([]);
    resetSaveAppointmentError();
    setStep(SchedulerStep.SELECT_SLOT);

    trackEvent(
      PatientScheduleAppointmentEvents.scheduleAppointmentModalAppointmentSelectionView,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );
  };

  const showMissingAttendeesWarning = useMemo<boolean>(() => {
    if (
      selectedAppointmentType?.attendeesAllowed ===
      AppointmentAttendeesAllowed.PROVIDER_ONLY
    ) {
      return !(
        hostParticipant &&
        providerParticipants.filter(({ isChecked }) => isChecked).length >= 1
      );
    }
    return (
      !!appointmentTypeExternalId &&
      !patientParticipant?.isChecked &&
      !supportParticipants.some((support: Participant) => support?.isChecked)
    );
  }, [
    appointmentTypeExternalId,
    patientParticipant,
    supportParticipants,
    providerParticipants,
  ]);

  const showDatePicker: boolean =
    step === SchedulerStep.SELECT_SLOT &&
    !noEligibleHostsError &&
    !isPendingAvailabilityQuery;

  const onTimezoneChange = (tzid: string): void => {
    setOptions((options) => ({ ...options, tzid }));
    setAppointmentStartDateTime(null);
  };

  if (
    showLoadingSpinner &&
    (isAppointmentTypesLoading || isPatientAppointmentsLoading)
  ) {
    return <Loading />;
  }

  const appointmentEndDateTime = getAppointmentEndTime(
    appointmentStartDateTime,
    selectedAppointmentType?.durationInMinutes,
  );

  const disableContinueButton =
    !appointmentStartDateTime ||
    isPendingAvailabilityQuery ||
    isSaveAppointmentLoading ||
    showMissingAttendeesWarning;

  const appointmentDuration = getDurationInMinutes(
    appointmentStartDateTime,
    appointmentEndDateTime,
  );

  const eventCardProps: EventCardProps = {
    eventType: 'appointment',
    scheduleItem: {
      attendees,
      eventDate: formatDateWithTimezone(
        appointmentStartDateTime,
        `${DateFormat.weekdayShortMonthDateNoSpecialChar}`,
        undefined,
      ),
      eventExternalId: selectedAppointmentType?.appointmentTypeExtId,
      eventName: `${selectedAppointmentType?.appointmentType}, ${appointmentDuration} minutes`,
      eventTime: `${formatAppointmentTimeFromDate(appointmentStartDateTime)} - ${formatAppointmentTimeFromDate(appointmentEndDateTime)}`,
      frequencyText: getRecurrenceDisplay(
        appointmentStartDateTime,
        recurrenceFrequency,
        DateTime.fromISO(appointmentStartDateTime)
          .plus({ months: 6, days: -1 })
          .toISODate(),
        true,
        false,
      ),
      patient: {
        age: getAgeFromDob(patient?.dateOfBirth),
        name: `${capitalizeFirstLetter(patient?.chosenName || patient?.firstName)} ${capitalizeFirstLetter(patient?.lastName)}`,
      },
      shouldShowViewPatientProfileButton: false,
      shouldShowZoomButton: false,
    },
  };

  const newScheduleSummary = () => {
    return <EventCard {...eventCardProps} />;
  };
  return (
    <SchedulerV2
      appointmentSummary={newScheduleSummary}
      conflicts={conflicts}
      currentDate={currentDate}
      defaultAppointmentType={appointmentTypeExternalId}
      disableContinueButton={disableContinueButton}
      disableScheduleButton={isSaveAppointmentLoading}
      handleNext={handleNext}
      handlePrevious={handlePrevious}
      handleProviderSelect={handleProviderSelect}
      handleRecurrenceChange={handleRecurrenceSelect}
      handleSave={handleSave}
      handleUpdateParticipant={handleUpdateParticipant}
      isAppointmentTypesLoading={isAppointmentTypesLoading}
      isCalendarConnected={isMyProfileConnected}
      isIgnoreAvailabilityChecked={ignoreAvailabilitySettings}
      isIgnoreAvailabilityDisabled={disableIgnoreAvailabilitySettings}
      isLoadingConflicts={isLoadingConflicts}
      isOpen={isOpen}
      onClose={onClose}
      onIgnoreAvailabilityChange={toggleIgnoreAvailabilitySettings}
      onSelectAppointmentType={(ruleExternalId) => {
        resetSelectedProviders();
        handleRuleSelect(ruleExternalId);
      }}
      onTimeZoneChange={onTimezoneChange}
      participantOptions={{
        hostParticipant,
        patientParticipant,
        providerParticipants,
        supportParticipants,
      }}
      showDatePicker={showDatePicker}
      showMissingAttendeesWarning={showMissingAttendeesWarning}
      sortedAppointmentTypes={sortedAppointmentTypes}
      step={step}
    />
  );
};

export default Scheduler;
